/**
 * Commander's intent, shared across the deployment.
 *
 * The one line every operator should be planning against — held in the shared
 * key-value store so it is the same on every screen and survives a refresh.
 * Any viewer reads it; an operator edits it, because the intent the whole team
 * flies to is not one seat's to change unseen.
 */

import { useCallback, useEffect, useState } from 'react';

import { getSharedState, setSharedState } from '@/api/state';
import { useHasRole } from '@/stores/authStore';

const KEY = 'commander.intent';
const REFRESH_MS = 5000;
const OK = 'oklch(0.78 0.15 155)';
const MUTED = 'rgba(244,244,245,0.5)';
const WHITE = '#f4f4f5';
const CARD = 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))';

export function CommanderIntent() {
  const canEdit = useHasRole('operator');
  const [value, setValue] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    void getSharedState().then((result) => {
      if (result.ok) setValue(result.data.state[KEY]?.value ?? '');
    });
  }, []);

  useEffect(() => {
    load();
    // Poll so a change made on another seat appears here without a refresh —
    // unless this operator is mid-edit, where clobbering their draft would be
    // the rude surprise a shared field must avoid.
    const id = window.setInterval(() => {
      if (!editing) load();
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load, editing]);

  if (value === null) return null;

  const save = () => {
    setValue(draft);
    setEditing(false);
    void setSharedState(KEY, draft);
  };

  return (
    <div
      className="rounded-[18px] border p-[16px_18px]"
      style={{ background: CARD, borderColor: `color-mix(in oklch, ${OK} 30%, transparent)` }}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="label-mono text-[9px]" style={{ color: OK, letterSpacing: '0.14em' }}>
          COMMANDER&apos;S INTENT
        </span>
        {canEdit && !editing ? (
          <button
            type="button"
            onClick={() => {
              setDraft(value);
              setEditing(true);
            }}
            className="label-mono ml-auto text-[8px]"
            style={{ color: MUTED }}
          >
            EDIT
          </button>
        ) : null}
      </div>

      {editing ? (
        <div className="flex flex-col gap-2">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={2}
            maxLength={4000}
            className="well rounded-[9px] px-2 py-1.5 text-[12px] outline-none"
            style={{ color: WHITE }}
          />
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={save}
              className="well rounded-[99px] px-3 py-1 label-mono text-[9px]"
            >
              SAVE
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="label-mono px-2 text-[9px]"
              style={{ color: MUTED }}
            >
              CANCEL
            </button>
          </div>
        </div>
      ) : (
        <p className="text-[13px] leading-snug" style={{ color: value ? WHITE : MUTED }}>
          {value || 'No intent set.'}
        </p>
      )}
    </div>
  );
}
